import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import {
  Autocomplete,
  Button,
  Checkbox,
  DialogTitle,
  Field,
  FieldsContainer,
  Form,
  FormSectionTitle,
  HtmlEditor,
  PageHeader,
  PasswordField,
  Spacer,
  Switch,
  TextField,
  Toolbar,
} from "common";
import I18n from "i18n-js";
import DirectorySelector from "./DirectorySelector";
import IntegrationIcon from "./IntegrationIcon";
import IntegrationHistoryList from "./history/IntegrationHistoryList";
import { IntegrationStatus, IntegrationType } from "./integrationApi";
import useIntegrationForm from "./useIntegrationForm";

export interface IntegrationFormProps {}

export default function IntegrationForm({}: IntegrationFormProps) {
  const {
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
    connect,
    disconnect,
    test,
    hasConnect,
    connected,
    fromRef,
    formAction,
    typeFromUrlParams,
    isUpdate,
    testing,
    setHistoryOpen,
    historyDialog,
    deleteItem,
  } = useIntegrationForm();

  const title = isCreate
    ? I18n.t("Integration.addTitle")
    : I18n.t("Integration.editTitle");
  const subtitle = options.type?.find(o => o.value === item.type)?.label;
  return (
    <>
      <form ref={fromRef} action={formAction} method="POST" />
      <PageHeader
        title={title}
        subtitle={subtitle}
        backUrl=".."
        avatar={
          item.type || typeFromUrlParams ? (
            <IntegrationIcon
              size="large"
              type={item.type || typeFromUrlParams}
            />
          ) : undefined
        }
      >
        <Switch
          id="enabled"
          label={I18n.t("Integration.attributes.enabled")}
          value={item.enabled as any}
          numeric
          onChange={(evt: any) =>
            onChange({ enabled: evt.target.value } as any, "enabled")
          }
        />
      </PageHeader>
      <Spacer h={2} />
      <Form
        loading={loading}
        item={item}
        options={options}
        errors={errors}
        onChange={onChange}
        translationCategory="Integration.attributes"
        translationHelpersCategory="Integration.helpers"
        isDirty={getIsDirty}
        requiredFields={getRequiredFields()}
      >
        <FieldsContainer>
          {isCreate && !typeFromUrlParams && (
            <Field fullWidth>
              <Autocomplete id="type" />
            </Field>
          )}
          <Field fullWidth>
            <TextField id="name" />
          </Field>
          <Field fullWidth>
            <Autocomplete id="filename_format" />
          </Field>
          <Field fullWidth>
            <Autocomplete id="location_ids" multiple />
          </Field>
          {item.type === IntegrationType.Ftp && (
            <>
              <FormSectionTitle title="Options" />
              <Field fullWidth>
                <TextField id="host" />
              </Field>
              <Field fullWidth>
                <TextField id="port" />
              </Field>
              <Field fullWidth>
                <TextField id="username" />
              </Field>
              <Field fullWidth>
                <PasswordField id="password" fake />
              </Field>
              <Field fullWidth>
                <DirectorySelector
                  id="directory"
                  integration_id={item.id}
                  disabled={!connected}
                />
              </Field>
              <Field fullWidth>
                <Checkbox id="ssl" numeric />
                <Checkbox id="passive" numeric />
              </Field>
            </>
          )}
          {[
            IntegrationType.GoogleDrive,
            IntegrationType.OneDrive,
            IntegrationType.Dropbox,
          ].includes(item.type) && (
            <>
              <FormSectionTitle title="Options" />
              <Field fullWidth>
                <DirectorySelector
                  id="directory"
                  integration_id={item.id}
                  disabled={!connected}
                />
              </Field>
            </>
          )}
          {[
            IntegrationType.Email,
            IntegrationType.MailChimp,
            IntegrationType.Smtp,
          ].includes(item.type) && (
            <>
              <FormSectionTitle title="Options" />
              {item.type === IntegrationType.MailChimp && (
                <>
                  <Field fullWidth>
                    <TextField id="api_key" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="from_name" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="from_email" />
                  </Field>
                </>
              )}
              {item.type === IntegrationType.Smtp && (
                <>
                  <Field fullWidth>
                    <TextField id="host" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="port" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="username" />
                  </Field>
                  <Field fullWidth>
                    <PasswordField id="password" fake />
                  </Field>
                  <Field fullWidth>
                    <TextField id="protocol" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="from_name" />
                  </Field>
                  <Field fullWidth>
                    <TextField id="from_email" />
                  </Field>
                </>
              )}
              <Field fullWidth>
                <TextField id="email" />
              </Field>
              <Field fullWidth>
                <TextField id="subject" />
              </Field>
              <Field fullWidth>
                <HtmlEditor id="message" minHeight={300} />
              </Field>
            </>
          )}
        </FieldsContainer>
        {isUpdate &&
          item.status !== IntegrationStatus.Connected &&
          [
            IntegrationType.Email,
            IntegrationType.Smtp,
            IntegrationType.Ftp,
            IntegrationType.MailChimp,
          ].includes(item.type) && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="error">
                Error happens check history for details.
              </Alert>
            </Box>
          )}
      </Form>
      <Spacer h={2} />
      <Toolbar>
        {isUpdate && (
          <>
            <Button
              title={I18n.t("App.delete")}
              color="error"
              onClick={() => deleteItem()}
              variant="outlined"
            />
            <Spacer w={2} />
            <Button
              title={I18n.t("Integration.history")}
              onClick={() => setHistoryOpen(true)}
              variant="outlined"
            />
          </>
        )}
        <Spacer right />
        {isUpdate && (
          <>
            <Button
              title={I18n.t("Integration.test")}
              disabled={!connected}
              onClick={() => test()}
              loading={testing}
              variant="outlined"
            />
            <Spacer w={2} />
          </>
        )}
        {hasConnect && isUpdate && (
          <>
            {connected ? (
              <Button
                title={I18n.t("Integration.connected")}
                //disabled //={!connected}
                sx={{ pointerEvents: "none" }}
                color="success"
                variant="outlined"
                //onClick={() => disconnect()}
                //loading={saving}
              />
            ) : (
              <Button
                title={I18n.t("Integration.connect")}
                disabled={connected}
                onClick={() => connect()}
                //loading={saving}
              />
            )}
            <Spacer w={2} />
          </>
        )}
        <Button
          title={I18n.t("App.save")}
          disabled={!isDirty}
          onClick={() => save()}
          loading={saving}
        />
      </Toolbar>
      <Dialog {...historyDialog} fullWidth maxWidth="md">
        <DialogTitle onClose={historyDialog.onClose}>History</DialogTitle>
        <DialogContent>
          <IntegrationHistoryList />
        </DialogContent>
        <DialogActions>
          <Button title={I18n.t("App.close")} />
        </DialogActions>
      </Dialog>
    </>
  );
}
